import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Flag, Trash2, Undo2, RefreshCw, MessageSquare, EyeOff, ShieldOff,
  CheckCircle2, AlertTriangle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListComments,
  adminRemoveComment, adminHideComment, adminSpamComment,
  adminApproveComment, adminRestoreComment,
  adminBulkComments,
} from "../../lib/api";

// Status taxonomy:
//   visible — published publicly
//   removed — soft-removed; public sees "[comment removed]"
//   hidden  — silently absent from public; admin sees full
//   spam    — silently absent from public; flagged as spam
//   held    — auto-quarantined by spam heuristics, awaiting decision
const STATUS_META = {
  visible: { label: "VISIBLE", color: "#5DD4B3" },
  removed: { label: "REMOVED", color: "#CBB892" },
  hidden:  { label: "HIDDEN",  color: "#8E8B83" },
  spam:    { label: "SPAM",    color: "#D92D20" },
  held:    { label: "HELD",    color: "#E0A526" },
};

const TABS = [
  { key: "reported", label: "Reported", filter: { reported_only: true } },
  { key: "held",     label: "Held",     filter: { status: "held" } },
  { key: "recent",   label: "Recent",   filter: { status: "visible" } },
  { key: "removed",  label: "Removed",  filter: { status: "removed" } },
  { key: "hidden",   label: "Hidden",   filter: { status: "hidden,spam" } },
  { key: "all",      label: "All",      filter: {} },
];

const Tag = ({ status, count }) => {
  const m = STATUS_META[status] || STATUS_META.visible;
  return (
    <span
      style={{ color: m.color, borderColor: m.color }}
      className="inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
    >
      {m.label}{count !== undefined ? ` · ${count}` : ""}
    </span>
  );
};

const formatTs = (iso) => (iso || "").slice(0, 19).replace("T", " ");

export const AdminCommentsPanel = ({ token, userToken = null }) => {
  const [tab, setTab] = useState("reported");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const tabSpec = useMemo(() => TABS.find((t) => t.key === tab) || TABS[0], [tab]);

  const load = useCallback(async () => {
    if (!token && !userToken) return;
    setLoading(true);
    try {
      const r = await adminListComments(token,
        { ...tabSpec.filter, limit: 200 }, userToken);
      setItems(r.comments || []);
      setCounts(r.status_counts || {});
      setSelected(new Set());
    } catch { toast.error("Could not load comments."); }
    finally { setLoading(false); }
  }, [token, userToken, tabSpec]);

  useEffect(() => { load(); }, [load]);

  const toggleSel = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((c) => c.id)));
  };

  const runOne = async (cid, action) => {
    const reasonNeeded = action === "remove" || action === "hide" || action === "spam";
    const reason = reasonNeeded
      ? window.prompt(`Optional reason for "${action}":`, "")
      : null;
    try {
      if (action === "remove")   await adminRemoveComment(token, cid, userToken, reason);
      if (action === "hide")     await adminHideComment(token, cid, userToken, reason);
      if (action === "spam")     await adminSpamComment(token, cid, userToken, reason);
      if (action === "approve")  await adminApproveComment(token, cid, userToken);
      if (action === "restore")  await adminRestoreComment(token, cid, userToken);
      toast.success(`Comment ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) { toast.message("Select comments first"); return; }
    if (!window.confirm(`Apply "${action}" to ${ids.length} comment${ids.length>1?"s":""}?`)) return;
    try {
      const r = await adminBulkComments(token, ids, action, userToken, null);
      toast.success(`${r.succeeded} comment${r.succeeded!==1?"s":""} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    }
  };

  return (
    <section className="mb-12" data-testid="admin-comments-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <MessageSquare size={14} /> // COMMENT MODERATION
          </div>
          <h2 className="font-display text-3xl text-[#E4DFD3]">
            {tabSpec.label} comments
          </h2>
          <div className="flex flex-wrap gap-2 mt-2 text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.2em]">
            {["visible","held","removed","hidden","spam","reported"].map((k) => (
              <Tag key={k} status={k === "reported" ? "spam" : k} count={counts[k] ?? 0} />
            ))}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={load} data-testid="admin-comments-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-[#2D2A26]">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-comments-tab-${t.key}`}
              className={
                "px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active
                  ? "border-[#D92D20] text-[#D92D20]"
                  : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Bulk action bar */}
      {items.length > 0 && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] cursor-pointer">
            <input
              type="checkbox"
              checked={selected.size === items.length && items.length > 0}
              onChange={toggleAll}
              data-testid="admin-comments-select-all"
              className="accent-[#D92D20]"
            />
            Select all ({selected.size}/{items.length})
          </label>
          <div className="flex-1" />
          <button
            onClick={() => runBulk("approve")} disabled={!selected.size}
            data-testid="bulk-approve"
            className="border border-[#5DD4B3]/40 hover:bg-[#0a1612] text-[#5DD4B3] disabled:opacity-30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
            <CheckCircle2 size={11} /> Approve
          </button>
          <button
            onClick={() => runBulk("remove")} disabled={!selected.size}
            data-testid="bulk-remove"
            className="border border-[#CBB892]/40 hover:bg-[#1a1408] text-[#CBB892] disabled:opacity-30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
            <Trash2 size={11} /> Soft remove
          </button>
          <button
            onClick={() => runBulk("hide")} disabled={!selected.size}
            data-testid="bulk-hide"
            className="border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] disabled:opacity-30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
            <EyeOff size={11} /> Hide
          </button>
          <button
            onClick={() => runBulk("spam")} disabled={!selected.size}
            data-testid="bulk-spam"
            className="border border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20] hover:text-white disabled:opacity-30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
            <ShieldOff size={11} /> Mark spam
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] text-[#757065] py-10 text-center font-mono text-xs uppercase tracking-[0.2em]"
             data-testid="admin-comments-empty">
          No comments in this view.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-comments-list">
          {items.map((c) => {
            const isHeld = c.status === "held";
            const isHidden = c.status === "hidden" || c.status === "spam";
            const isRemoved = c.status === "removed";
            const isVisible = c.status === "visible";
            return (
              <li key={c.id}
                  data-testid={`admin-comment-${c.id}`}
                  className="border border-[#2D2A26] bg-[#131313] p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleSel(c.id)}
                    data-testid={`select-${c.id}`}
                    className="mt-1 accent-[#D92D20]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Tag status={c.status} />
                      {c.reported_count > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#D92D20] text-white font-mono text-[9px] uppercase tracking-[0.25em]">
                          <Flag size={9} /> {c.reported_count} report{c.reported_count > 1 ? "s" : ""}
                        </span>
                      )}
                      {c.spam_reason && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#E0A526]/40 text-[#E0A526] font-mono text-[9px] uppercase tracking-[0.25em]"
                              title={`spam_reason: ${c.spam_reason}`}>
                          <AlertTriangle size={9} /> {c.spam_reason}
                        </span>
                      )}
                      <span className="text-[13px] text-[#E4DFD3] font-medium">{c.user_name || "[anon]"}</span>
                      {c.user_email && (
                        <span className="font-mono text-[10px] text-[#757065]">{c.user_email}</span>
                      )}
                      <span className="font-mono text-[10px] text-[#757065]">·</span>
                      {c.topic_slug ? (
                        <a href={`/topic/${c.topic_slug}`} target="_blank" rel="noopener noreferrer"
                           className="font-mono text-[10px] text-[#A39E93] hover:text-[#D92D20] inline-flex items-center gap-1">
                          “{c.topic_title}” <ExternalLink size={9} />
                        </a>
                      ) : (
                        <span className="font-mono text-[10px] text-[#757065]">“{c.topic_title}”</span>
                      )}
                      <span className="font-mono text-[10px] text-[#757065] ml-auto">{formatTs(c.created_at)}</span>
                    </div>
                    <div className={"text-[14px] whitespace-pre-wrap break-words " + (isVisible ? "text-[#E4DFD3]" : "text-[#A39E93]")}>
                      {c.body || <span className="italic text-[#757065]">[empty]</span>}
                    </div>
                    {c.moderation_reason && (
                      <div className="font-mono text-[10px] text-[#757065] mt-2">
                        // reason: {c.moderation_reason}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {isHeld && (
                        <button onClick={() => runOne(c.id, "approve")}
                          data-testid={`approve-${c.id}`}
                          className="flex items-center gap-1 border border-[#5DD4B3]/40 hover:bg-[#0a1612] text-[#5DD4B3] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                          <CheckCircle2 size={11} /> Approve
                        </button>
                      )}
                      {(isHidden || isRemoved) && (
                        <button onClick={() => runOne(c.id, "restore")}
                          data-testid={`restore-${c.id}`}
                          className="flex items-center gap-1 border border-[#454038] hover:border-[#5DD4B3] text-[#5DD4B3] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                          <Undo2 size={11} /> Restore
                        </button>
                      )}
                      {(isVisible || isHeld) && (
                        <button onClick={() => runOne(c.id, "remove")}
                          data-testid={`remove-${c.id}`}
                          className="flex items-center gap-1 border border-[#CBB892]/40 hover:bg-[#1a1408] text-[#CBB892] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                          <Trash2 size={11} /> Soft remove
                        </button>
                      )}
                      {(isVisible || isRemoved || isHeld) && (
                        <button onClick={() => runOne(c.id, "hide")}
                          data-testid={`hide-${c.id}`}
                          className="flex items-center gap-1 border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                          <EyeOff size={11} /> Hide
                        </button>
                      )}
                      {c.status !== "spam" && (
                        <button onClick={() => runOne(c.id, "spam")}
                          data-testid={`spam-${c.id}`}
                          className="flex items-center gap-1 border border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20] hover:text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                          <ShieldOff size={11} /> Mark spam
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default AdminCommentsPanel;
